import debounce from 'lodash/debounce';
import { nextTick, onMounted, onUnmounted, reactive, ref, unref, watch } from 'vue';

let areScrollEventListenersEnabled;
let scrollPosition;
let oldLeft;
const isScrolling = ref(false);

export default function useScroll ({ init = false, isExternalPortal = false } = {}) {
    const debouncedStoppedScrolling = debounce(
        () => {
            isScrolling.value = false;
        },
        200,
        {
            leading: false,
            trailing: true,
        },
    );

    if (init) {
        areScrollEventListenersEnabled = ref(true);
        scrollPosition = reactive({
            left: 0,
            top: 0,
        });

        const keydownEventListener = (event) => {
            if (!unref(areScrollEventListenersEnabled)) {
                const keyCodesToBlock = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Space'];

                if (keyCodesToBlock.includes(event.code)) {
                    event.preventDefault();
                }
            }
        };

        const pageScrollEventListener = (event) => {
            if (!unref(areScrollEventListenersEnabled)) return;

            const newScrollTop = isExternalPortal ? window.scrollY : event.currentTarget.scrollTop;

            if (scrollPosition.top !== newScrollTop) {
                scrollPosition.top = newScrollTop;
            }
        };

        let scrollContainer;

        onMounted(() => {
            nextTick(() => {
                scrollContainer = isExternalPortal ? document : document.querySelector('.sub-route-layout__leading') || document.querySelector('.pendo-page-container');
                window.addEventListener('keydown', keydownEventListener, true);
                scrollContainer.addEventListener('scroll', pageScrollEventListener, true);
            });
        });

        onUnmounted(() => {
            window.removeEventListener('keydown', keydownEventListener, true);
            scrollContainer.removeEventListener('scroll', pageScrollEventListener, true);
            scrollPosition.left = 0;
            scrollPosition.top = 0;
        });

        watch(
            scrollPosition,
            (newPosition) => {
                // old & new for nested properties inside a reactive always return the same value
                // Keep track separately so we can tell if we are scrolling horizontally
                if (newPosition.left === oldLeft) return;

                isScrolling.value = true;
                debouncedStoppedScrolling();

                oldLeft = newPosition.left;
            },
            { immediate: false },
        );
    }

    function setScrollPosition ({ left, top }) {
        scrollPosition.left = left ?? scrollPosition.left;
        scrollPosition.top = top ?? scrollPosition.top;
    }

    function enableScrollEventListeners () {
        areScrollEventListenersEnabled.value = true;
    }

    function preventScrollEventListeners () {
        areScrollEventListenersEnabled.value = false;
    }

    function scrollToTop () {
        const container = document.querySelector('.sub-route-layout__leading') || document.querySelector('.pendo-page-container');
        container.scrollTo({ top: 0, behavior: 'smooth' });
    }

    function horizontalScrollTo (newLeft, { duration = 400, onScrollFinished = () => {} } = {}) {
        if (!unref(areScrollEventListenersEnabled)) return;

        // Disable scroll duration in cypress to avoid flakiness
        const effectiveDuration = window.Cypress ? 1 : duration;

        const startLeft = scrollPosition.left;
        const distance = newLeft - startLeft;
        let startTime;

        preventScrollEventListeners();

        function step (currentTime) {
            if (startTime === undefined) {
                startTime = currentTime;
            }

            const elapsed = currentTime - startTime;
            const t = Math.min(elapsed / effectiveDuration, 1); // Normalized time (0 to 1)

            scrollPosition.left = startLeft + distance * t; // linear easing

            if (t < 1) {
                requestAnimationFrame(step);
            } else {
                enableScrollEventListeners();
                onScrollFinished();
            }
        }

        requestAnimationFrame(step);
    }

    // If a swimlane is added or the order of the swimlanes changes then the dom nodes are updated.
    // This resets the scroll position of the horizontal-scroll-sync components.
    // Changing the scrollPosition triggers the watcher in horizontal-scroll-sync to resync the swimlane scroll positions.
    function resetHorizontalScrollSyncComponents () {
        setScrollPosition({ left: scrollPosition.left - 1 });
        nextTick(() => setScrollPosition({ left: scrollPosition.left + 1 }));
    }

    return {
        scrollPosition,
        areScrollEventListenersEnabled,

        setScrollPosition,
        enableScrollEventListeners,
        preventScrollEventListeners,
        scrollToTop,
        horizontalScrollTo,
        resetHorizontalScrollSyncComponents,
        isScrolling,
    };
}
